import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { 
  Home, 
  Calendar,
  CalendarDays, 
  Clock, 
  Users, 
  Settings,
  LogOut,
  LayoutDashboard,
  ClipboardList,
  UserCircle,
  Moon,
  Sun,
  Building2, UserRound,
  Car,
  FileText,
  MapPin,
  ReceiptText,
  Award,
  BadgeDollarSign
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_care-timesheet-1/artifacts/8omtdhmr_CRUZZN%20CARE%20LOGO.jpg';

export const Layout = ({ children }) => {
  const { user, logout, canManage, isStaff, theme, updateTheme } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleThemeToggle = () => {
    updateTheme(theme === 'light' ? 'dark' : 'light');
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case ROLES.SUPER_ADMIN:
        return 'Super Admin';
      case ROLES.ADMIN:
        return 'Admin';
      default:
        return 'Staff';
    }
  };

  // Staff navigation items
  const staffNav = [
    { to: '/', icon: Home, label: 'Dashboard' },
    { to: '/visits', icon: CalendarDays, label: 'Visits' },
    { to: '/mileage', icon: Car, label: 'Mileage' },
    { to: '/timesheets', icon: Clock, label: 'Timesheets' },
    { to: '/settings', icon: Settings, label: 'Settings' },
  ];

  // Manager/Admin navigation items
  const managerNav = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/roster', icon: CalendarDays, label: 'Workforce Planner' },
    { to: '/visits', icon: CalendarDays, label: 'Visits' },
    { to: '/participants', icon: UserRound, label: 'Participants' },
    { to: '/clients', icon: Building2, label: 'Clients' },
    { to: '/staff', icon: Users, label: 'Staff' },
    { to: '/mileage', icon: Car, label: 'Mileage' },
    { to: '/timesheets', icon: ClipboardList, label: 'Timesheets' },
    { to: '/shift-reports', icon: FileText, label: 'Shift Reports' },
    { to: '/payroll-centre', icon: ReceiptText, label: 'Payroll Centre' },
    { to: '/award-rates', icon: Award, label: 'Award Rates' },
    { to: '/staff-payroll', icon: BadgeDollarSign, label: 'Staff Payroll' },
    { to: '/live-staff-map', icon: MapPin, label: 'Live Staff Map' },
    { to: '/settings', icon: Settings, label: 'Settings' },
  ];

  const navItems = isStaff ? staffNav : managerNav;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="Cruzzn Care" 
              className="h-10 w-10 rounded-lg object-cover"
            />
            <div className="hidden sm:block">
              <h1 className="font-heading font-bold text-lg text-foreground">Cruzzn Care</h1>
              <p className="text-xs text-muted-foreground">Workforce Platform</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Theme Toggle */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleThemeToggle}
              data-testid="header-theme-toggle"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-foreground">
                      {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <span className="text-sm font-medium block">{user?.full_name}</span>
                    <span className="text-xs text-muted-foreground">{getRoleLabel(user?.role)}</span>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.full_name}</p>
                  <p className="text-xs text-muted-foreground">{getRoleLabel(user?.role)}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleThemeToggle}>
                  {theme === 'dark' ? (
                    <>
                      <Sun className="w-4 h-4 mr-2" />
                      Light Mode
                    </>
                  ) : (
                    <>
                      <Moon className="w-4 h-4 mr-2" />
                      Dark Mode
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-menu-item">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="pb-20 md:pb-6">
        {children}
      </main>

      {/* Bottom Navigation - Mobile */}
      <nav className="bottom-nav md:hidden" data-testid="bottom-navigation">
        {navItems.slice(0, 5).map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `bottom-nav-item ${isActive ? 'active' : ''}`
            }
            data-testid={`nav-${label.toLowerCase()}`}
          >
            <Icon className="w-5 h-5" />
            <span className="text-xs mt-1">{label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Side Navigation - Desktop */}
      <aside className="hidden md:flex fixed left-0 top-[65px] bottom-0 w-64 border-r border-border bg-card flex-col p-4">
        <nav className="space-y-1 flex-1">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition-colors ${
                  isActive 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`
              }
              data-testid={`sidebar-nav-${label.toLowerCase()}`}
            >
              <Icon className="w-5 h-5" />
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Desktop content offset */}
      <style>{`
        @media (min-width: 768px) {
          main {
            margin-left: 256px;
          }
        }
      `}</style>
    </div>
  );
};

export default Layout;
